import { action, autorun, computed, observable, runInAction } from 'mobx';
import React from 'react';
import { refreshServerUpgradeStatusConfig } from '../../config/app';
import { vendorsUrl } from '../../config/vendors';
import { checkSinglePermission } from '../../security/security-hook';
import AbstractStore from '../../store/AbstractStore';
import { PracticeConfig } from '../../types/practice-types';
import {
  PERMISSION_ALLOW,
  PERMISSION_VIEW,
  RESOURCE_DASHBOARD_BUTTON_VELLO,
  RESOURCE_LEFT_NAV_PRACTICE_USERS_MNGMNT,
  Sap,
} from '../../types/users-types';
import { VelloEnrollmentStatus } from '../../types/vello-types';
import { isDefined, sleep } from '../../utils/object-utils';
import { openInANewWindow } from '../../utils/url-utils';
import {
  MessageCorrectVelloPractice,
  MessageMultipleServers,
  MessageNoServer,
  MessageNoSupportContact,
  MessageNoUpgradeRequired,
  MessagePracticeUpgradeLocked,
  MessageSendVerificationCode,
  MessageServerOffline,
  MessageServerStatusUnavailable,
  MessageSupportAssistanceRequired,
  MessageSupportStatusUnknown,
  MessageWithoutPracticeAdmin,
  MessageWithPracticeAdmin,
  VelloIntegrationUnavailable,
} from './renderers';
import VerificationCodeModalContent from './VerificationCodeModalContent';

interface Loaders {
  page: boolean;
  scheduleNow?: boolean;
  userManagement?: boolean;
}

interface VelloAuthLink {
  loading: boolean;
  status: VelloEnrollmentStatus | 'Integration Unavailable' | 'Connection Error';
  authLink: string;
}

export default class DashboardStore extends AbstractStore {
  @observable loaders: Loaders = { page: true };

  @observable velloAuthLinks: Record<string, VelloAuthLink> = {};

  private autorunDisposal: ReturnType<typeof autorun> | undefined;

  @computed
  get buttonsDisabled(): boolean {
    return !!(this.loaders.scheduleNow || this.loaders.userManagement);
  }

  @computed
  public get sapIds(): string[] {
    return this.root.globalStore.userSaps.map(a => a.id) || [];
  }

  @action
  public async init(): Promise<void> {
    runInAction(() => {
      this.loaders = { page: true };
    });
    await this.root.globalStore.endUpgradeTaskSessionIfStarted();
    if (this.root.globalStore.userSaps.length === 0) {
      try {
        runInAction(() => (this.loaders.page = true));
        await this.root.apiStore.fetchCurrentUserDetails();
        if (this.root.globalStore.userSaps && this.root.globalStore.userSaps.length > 0) {
          runInAction(
            () => (this.root.globalStore.selectedSap = this.root.globalStore.userSaps[0]),
          );
        }
      } catch {
        // ignore
      }
    }
    runInAction(() => (this.loaders.page = false));
    this.autorunDisposal = autorun(async () => {
      await this.fetchVelloAuthUrl(this.root.globalStore.selectedSap);
    });
  }

  @action
  public async checkAllowStartScheduleUpgrade(
    index: number,
    isAllowed: boolean,
    bypassAcknowledgements: boolean,
  ): Promise<void> {
    runInAction(() => (this.loaders.scheduleNow = true));
    const sap = this.root.globalStore.userSaps[index];
    const sapId = sap?.id;
    if (!sapId) return;
    if (isAllowed) {
      return this.startScheduleUpgrade(index, bypassAcknowledgements);
    }
    const practiceAdminExists = await this.isPracticeAdminExists(sapId);
    this.root.globalStore.showOkPopup(
      "Let's get started ...",
      React.createElement(
        practiceAdminExists ? MessageWithPracticeAdmin : MessageWithoutPracticeAdmin,
      ),
    );
    runInAction(() => (this.loaders.scheduleNow = false));
  }

  @action
  public async verificationCodeModal(sapId: string): Promise<void> {
    try {
      await this.root.apiStore.updatePracticeAdminVerificationCode(sapId);
      this.root.globalStore.showOkCancelPopup(
        'Enter Your Code',
        React.createElement(VerificationCodeModalContent),
        'Submit',
        'Resend Code',
        () => {},
      );
    } catch (e: any) {
      this.showPracticeRelatedPopup(e);
    }
  }

  @action
  public async checkAllowUserManagement(index: number, isAllowed: boolean): Promise<void> {
    runInAction(() => (this.loaders.userManagement = true));
    const sap = this.root.globalStore.userSaps[index];
    const sapId = sap?.id;
    if (!sapId) return;
    if (isAllowed) {
      this.navigate('/practice/user-management');
    } else {
      if (await this.isPracticeAdminExists(sapId)) {
        this.root.globalStore.showOkPopup(
          "Let's get started ...",
          React.createElement(MessageWithPracticeAdmin),
        );
      } else {
        const agentsKaseyaInfo = await this.root.apiStore.getAgentsKaseyaInfo(sapId, false);
        if (!isDefined(agentsKaseyaInfo.machineGroupName)) {
          this.root.globalStore.showOkPopup(
            'Support Assistance Required',
            React.createElement(MessageSupportAssistanceRequired),
          );
        } else {
          this.root.globalStore.showOkCancelPopup(
            'Create Practice Administrator',
            React.createElement(MessageSendVerificationCode, { practiceName: sap.name }),
            'Send Code',
            'Cancel',
            () => this.verificationCodeModal(sap.id),
            () => {},
            false,
            false,
          );
        }
      }
    }
    runInAction(() => (this.loaders.userManagement = false));
  }

  @action
  public redirectToTrupanion(index: number): void {
    const sap = this.root.globalStore.userSaps[index];
    window.location.href = `${vendorsUrl()}?sapId=${sap.id}`;
  }

  @action
  public async fetchVelloAuthUrl(sap: Sap): Promise<void> {
    if (this.velloAuthLinks[sap.id]) return;
    const velloAuthLink: VelloAuthLink = { loading: true, status: 'ENROLLED', authLink: '' };
    runInAction(() => {
      this.velloAuthLinks[sap.id] = velloAuthLink;
    });
    const check = checkSinglePermission(
      this.root.apiStore.userGlobalPermissions,
      RESOURCE_DASHBOARD_BUTTON_VELLO,
      PERMISSION_ALLOW,
    );
    if (!check && !sap.myIdexxAdmin) {
      velloAuthLink.status = 'Integration Unavailable';
    } else {
      try {
        const response = await this.root.apiStore.getVelloEnrollmentAndPracticeAuthUrl(sap.id);
        velloAuthLink.status = response.status;
        velloAuthLink.authLink = response.authLink || '';
      } catch {
        velloAuthLink.status = 'Connection Error';
      }
    }
    velloAuthLink.loading = false;
    runInAction(() => {
      this.velloAuthLinks[sap.id] = velloAuthLink;
    });
  }

  @action
  public launchVelloAuthPage(sapId: string): void {
    const statusObject = this.velloAuthLinks[sapId];
    if (!statusObject) return;
    switch (this.velloAuthLinks[sapId].status) {
      case 'ENROLLED':
        openInANewWindow(statusObject.authLink);
        break;
      case 'NOT_ENROLLED':
        this.root.globalStore.showOkPopup('Vello is not enrolled', 'Not enrolled');
        break;
      case 'ENROLLMENT_ERROR':
        this.root.globalStore.showOkPopup('Vello Enrollment Error', 'Enrollment error');
        break;
      case 'NO_PRACTICE_CODE':
        this.root.globalStore.showOkPopup(
          'Additional Setup Required',
          'Additional setup is required, please contact Cornerstone Support.',
        );
        break;
      case 'PRACTICE_NOT_FOUND':
        this.root.globalStore.showOkPopup(
          'Confirm Practice',
          React.createElement(MessageCorrectVelloPractice),
          () => openInANewWindow(statusObject.authLink),
        );
        break;
      case 'Integration Unavailable':
        this.root.globalStore.showOkCancelPopup(
          'Integration Unavailable',
          React.createElement(VelloIntegrationUnavailable),
          'OK',
          'Sign Out',
          () => {},
          () => this.root.globalStore.signOut(),
          false,
          true,
          true,
        );
        break;
      case 'Connection Error':
        this.root.globalStore.showOkPopup(
          'Connection Error',
          'An unexpected error occurred, please try again.',
        );
        break;
    }
  }

  @action
  public close(): void {
    this.autorunDisposal?.();
    this.loaders = { page: true };
  }

  @action
  private async startScheduleUpgrade(
    index: number,
    bypassAcknowledgements: boolean,
  ): Promise<void> {
    runInAction(() => {
      this.loaders.scheduleNow = true;
      this.root.globalStore.practiceSelectorDisabled = true;
    });
    const affiliation = this.root.globalStore.userSaps[index];
    const sapId = affiliation?.id;
    if (!sapId) return;
    try {
      await this.root.globalStore.fetchEventIntervals();
      await this.root.globalStore.fetchPracticeData(this.sapIdChanged, true);
      this.root.globalStore.isLoggedIn = true;
      const { practice } = this.root.globalStore;
      if (practice) {
        if (practice.isLocked && practice.lockedByEmail !== this.root.apiStore.userEmail) {
          this.root.globalStore.showOkPopup(
            'Cornerstone Upgrade',
            React.createElement(MessagePracticeUpgradeLocked, { userName: practice.lockedByName }),
          );
        } else if (
          !['fileDistribution', 'complete', 'error', ''].includes(
            practice.upgradeProcess?.phase || '',
          )
        ) {
          this.root.globalStore.showOkPopup(
            'Upgrade In Progress',
            'Your Cornerstone upgrade is currently in progress. Please try again after your upgrade contacts receive notification that the upgrade was completed.',
          );
        } else {
          await this.checkServerUpgradeStateAndRedirect(practice, bypassAcknowledgements);
        }
      }
    } catch (e: any) {
      this.showPracticeRelatedPopup(e);
    }
    runInAction(() => {
      this.loaders.scheduleNow = false;
      this.root.globalStore.practiceSelectorDisabled = false;
    });
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private async checkServerUpgradeStateAndRedirect(
    practice: PracticeConfig,
    bypassAcknowledgements: boolean,
  ): Promise<void> {
    if (!practice.serverAgentId || !practice.serverStatus) {
      this.navigate(bypassAcknowledgements ? '/check-qualifications' : '/acknowledgments');
      return;
    }
    switch (practice.serverStatus) {
      case 'UPGRADE_NOT_SCHEDULED':
        this.navigate(bypassAcknowledgements ? '/check-qualifications' : '/acknowledgments');
        break;
      case 'UPGRADE_PENDING':
        this.navigate('/confirmation');
        break;
      case 'UPGRADE_COMPLETE':
        this.showNoUpgradeRequiredPopup();
        break;
      case 'OFFLINE':
        this.showUnableToDetermineStatusPopup();
        break;
      case 'REFRESH_IN_PROGRESS': {
        for (let i = 0; i < refreshServerUpgradeStatusConfig.maxAttempts; i++) {
          if (i > 0) await sleep(refreshServerUpgradeStatusConfig.interval);
          try {
            const state = await this.root.apiStore.getAgentUpgradeState(
              this.sapId,
              practice.serverAgentId,
            );
            if (state === 'UPGRADE_FAILED') {
              this.navigate('/confirmation');
              return;
            }
            if (state === 'UPGRADE_COMPLETE') {
              this.showNoUpgradeRequiredPopup();
              return;
            }
          } catch {
            /* ignore */
          }
        }
        this.showUnableToDetermineStatusPopup();
      }
    }
  }

  private showUnableToDetermineStatusPopup(): void {
    this.root.globalStore.showOkPopup(
      'Unable To Determine Upgrade Status',
      React.createElement(MessageServerStatusUnavailable),
    );
  }

  private showNoUpgradeRequiredPopup(): void {
    this.root.globalStore.showOkPopup(
      'No Upgrade Required',
      React.createElement(MessageNoUpgradeRequired),
    );
  }

  private isPracticeAdminExists(sapId: string): Promise<boolean> {
    return this.root.apiStore.checkSapUsersForResourcePermission(
      sapId,
      RESOURCE_LEFT_NAV_PRACTICE_USERS_MNGMNT,
      PERMISSION_VIEW,
    );
  }

  private showPracticeRelatedPopup(e: any): void {
    if (e.message?.includes('Support Contract Required')) {
      this.root.globalStore.showOkPopup(
        'Support Contract Required',
        React.createElement(MessageNoSupportContact),
      );
    } else if (e.message?.includes('Support Status Unknown')) {
      this.root.globalStore.showOkPopup(
        'Support Status Unknown',
        React.createElement(MessageSupportStatusUnknown),
      );
    } else if (e.message?.includes('Server offline')) {
      this.root.globalStore.showOkPopup(
        'Server Offline',
        React.createElement(MessageServerOffline),
      );
    } else if (e.message?.includes('Multiple servers detected')) {
      this.root.globalStore.showOkPopup(
        'Multiple servers found',
        React.createElement(MessageMultipleServers),
      );
    } else if (e.message?.includes('No server detected')) {
      this.root.globalStore.showOkPopup('No Server Detected', React.createElement(MessageNoServer));
    } else {
      this.root.globalStore.showOkPopup('Error', e.message);
    }
  }
}
